import React, { useState, useEffect } from 'react'
import { Settings, Download, Trash2, Calendar } from 'lucide-react'

function Configurations() {
  const [configurations, setConfigurations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadConfigurations()
  }, [])

  const loadConfigurations = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/configurations')
      if (!response.ok) throw new Error('Failed to load configurations')
      const data = await response.json()
      setConfigurations(data)
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const exportConfiguration = (config) => {
    const exportData = {
      name: config.name,
      provider: config.provider?.name,
      vcpus: config.vcpus,
      ramGb: config.ramGb,
      storageGb: config.storageGb,
      instanceType: config.instanceType,
      monthlyCost: config.costData?.monthly,
      compliance: {
        gdpr: config.gdprCompliant,
        nonUsCloudAct: config.nonUsCloudAct,
      },
      exportedAt: new Date().toISOString(),
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${config.name.replace(/\s+/g, '-').toLowerCase()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="min-h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading configurations...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-800">Error: {error}</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Saved Configurations
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Manage your saved cloud infrastructure configurations. 
          Export, compare, or use them as templates for new deployments.
        </p>
      </div>

      {configurations.length === 0 ? (
        <div className="text-center py-12">
          <Settings className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Configurations Saved
          </h3>
          <p className="text-gray-600 mb-6">
            Start by creating and saving a configuration from the main page.
          </p>
          <a
            href="/"
            className="btn-primary inline-flex items-center space-x-2"
          >
            <span>Create Configuration</span>
          </a>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {configurations.map((config) => (
            <div key={config.id} className="card hover:shadow-xl transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">{config.name}</h3>
                  <p className="text-sm text-gray-500">{config.provider?.name}</p>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-primary-600">{config.vcpus}</div>
                    <div className="text-gray-500">vCPUs</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-primary-600">{config.ramGb}GB</div>
                    <div className="text-gray-500">RAM</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-primary-600">{config.storageGb}GB</div>
                    <div className="text-gray-500">Storage</div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Monthly Cost:</span>
                    <span className="font-semibold text-gray-900">
                      {config.costData ? formatCurrency(config.costData.monthly) : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2 text-xs text-gray-500 mb-4">
                <Calendar className="h-3 w-3" />
                <span>Created {formatDate(config.createdAt)}</span>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => exportConfiguration(config)}
                  className="flex-1 flex items-center justify-center space-x-2 py-2 px-3 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <Download className="h-4 w-4" />
                  <span>Export</span>
                </button>
                <button
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this configuration?')) {
                      setConfigurations(configs => configs.filter(c => c.id !== config.id))
                    }
                  }}
                  className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Configurations 